<template>
  <v-dialog :value="value" max-width="1160px" scrollable @input="$emit('input', $event)">
    <v-card v-if="value">
      <!-- header: what this number is + the totals it must add up to -->
      <div class="bm-head">
        <v-avatar size="40" :color="mode.avatar" class="mr-4">
          <v-icon :color="mode.icon">{{ mode.mdi }}</v-icon>
        </v-avatar>
        <div class="flex-grow-1" style="min-width: 0">
          <div class="bm-head-title">{{ mode.title }} · {{ subjectLabel }}</div>
          <div class="bm-tiny mt-1" v-html="mode.description"></div>
        </div>

        <div v-if="noPo" class="d-flex text-right flex-shrink-0" style="gap: 20px">
          <div>
            <div class="bm-tiny">Belanja PO supplier</div>
            <div class="bm-num subtitle-1">Rp {{ formatIdr(noPoTotals.SPEND) }}</div>
          </div>
          <div>
            <div class="bm-tiny">Pemakaian stok</div>
            <div class="bm-num subtitle-1">Rp {{ formatIdr(noPoTotals.STOCK) }}</div>
          </div>
          <div class="pl-5" style="border-left: thin solid rgba(0, 0, 0, 0.12)">
            <div class="bm-tiny">Job tanpa PO CRM</div>
            <div class="bm-num bm-total">{{ noPoJobCount }} job</div>
            <div class="bm-tiny">
              {{ groupsFiltered.length }}
              {{ isItem ? "item" : isStock ? "MR" : "PO supplier" }} ditampilkan
            </div>
          </div>
        </div>
        <div v-else-if="isBom" class="d-flex text-right flex-shrink-0" style="gap: 20px">
          <div>
            <div class="bm-tiny">Kebutuhan BOM</div>
            <div class="bm-num grey--text text--darken-2 subtitle-1">
              Rp {{ formatIdr(bomNeedTotal) }}
            </div>
          </div>
          <div>
            <div class="bm-tiny">Sudah tercakup (MR · PO · PR)</div>
            <div class="bm-num grey--text text--darken-2 subtitle-1">
              − Rp {{ formatIdr(bomCoveredTotal) }}
            </div>
          </div>
          <div class="pl-5" style="border-left: thin solid rgba(0, 0, 0, 0.12)">
            <div class="bm-tiny">Sisa BOM (proyeksi belanja)</div>
            <div class="bm-num bm-total">Rp {{ formatIdr(sum) }}</div>
            <div class="bm-tiny">
              {{ bomItemCount }} item<span v-if="bomNoPriceCount">
                · {{ bomNoPriceCount }} tanpa harga</span
              >
              · {{ matchCopy }}
            </div>
          </div>
        </div>
        <div v-else-if="isStock" class="d-flex text-right flex-shrink-0" style="gap: 20px">
          <div>
            <div class="bm-tiny">Nilai jurnal MR (bruto)</div>
            <div class="bm-num grey--text text--darken-2 subtitle-1">
              Rp {{ formatIdr(journalTotal) }}
            </div>
          </div>
          <div>
            <div class="bm-tiny">Terdedup dengan PO job</div>
            <div class="bm-num grey--text text--darken-2 subtitle-1">
              − Rp {{ formatIdr(dedupTotal) }}
            </div>
          </div>
          <div class="pl-5" style="border-left: thin solid rgba(0, 0, 0, 0.12)">
            <div class="bm-tiny">Stok neto {{ toLabel }}</div>
            <div class="bm-num bm-total">Rp {{ formatIdr(sum) }}</div>
            <div class="bm-tiny">{{ mrCount }} MR · {{ matchCopy }}</div>
          </div>
        </div>
        <div v-else class="text-right flex-shrink-0">
          <div class="bm-tiny">
            {{ bucket === "COMMITTED" ? "Committed" : "Total" }} {{ toLabel }}
          </div>
          <div
            class="bm-num bm-total"
            :class="{ 'grey--text text--darken-2': bucket === 'COMMITTED' }"
          >
            Rp {{ formatIdr(sum) }}
          </div>
          <div class="bm-tiny">
            <template v-if="bucket === 'COMMITTED'">
              {{ groupsFiltered.length }} dokumen<span v-if="committedRatio !== null">
                · bila jadi PO, rasio menjadi {{ formatPct(committedRatio) }}</span
              >
            </template>
            <template v-else>
              {{ groupsFiltered.length }} PO supplier · {{ jobCountLabel }} ·
              {{ estimatedCopy }}
            </template>
          </div>
        </div>
        <v-btn icon class="ml-2 flex-shrink-0" @click="$emit('input', false)"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </div>
      <v-divider></v-divider>

      <!-- toolbar: what is shown -->
      <div class="bm-toolbar">
        <v-btn-toggle v-if="noPo" v-model="bucket" mandatory dense color="primary" @change="load">
          <v-btn value="SPEND" small>Belanja PO supplier</v-btn>
          <v-btn value="STOCK" small>Stok / MR</v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          v-else-if="!isStock && !isBom"
          v-model="bucket"
          mandatory
          dense
          color="primary"
          @change="load"
        >
          <v-btn value="SPEND" small>Belanja · PO approve</v-btn>
          <v-btn value="COMMITTED" small>Committed · PR / PO belum approve</v-btn>
        </v-btn-toggle>
        <span v-if="isStock" class="bm-cap mr-2">KELOMPOKKAN</span>
        <v-btn-toggle
          v-if="isStock"
          v-model="groupBy"
          mandatory
          dense
          color="indigo darken-1"
          @change="load"
        >
          <v-btn value="MR" small>Per MR</v-btn>
          <v-btn value="ITEM" small>Per item stok</v-btn>
        </v-btn-toggle>

        <v-select
          v-if="isBom && panelOptions.length > 1"
          v-model="panelFilter"
          :items="panelOptions"
          label="Panel code"
          outlined
          dense
          hide-details
          class="bm-w-170"
        ></v-select>
        <v-select
          v-if="jobOptions.length > 1"
          v-model="jobFilter"
          :items="jobOptions"
          label="Job"
          outlined
          dense
          hide-details
          class="bm-w-170"
        ></v-select>
        <v-text-field
          v-model="search"
          :label="searchLabel"
          outlined
          dense
          hide-details
          clearable
          append-icon="mdi-magnify"
          class="bm-w-260"
        ></v-text-field>
        <v-checkbox
          v-if="isStock"
          v-model="hideDedup"
          label="Sembunyikan terdedup"
          dense
          hide-details
          class="mt-0"
        ></v-checkbox>
        <v-spacer></v-spacer>
        <span class="bm-tiny">{{
          isItem
            ? "Klik baris untuk melihat MR asal."
            : isBom
            ? "Klik baris panel code untuk melihat item."
            : "Klik baris untuk melihat item."
        }}</span>
      </div>
      <v-divider></v-divider>

      <v-card-text class="pa-0 bm-body">
        <v-data-table
          :headers="groupHeaders"
          :items="groupsFiltered"
          item-key="key"
          show-expand
          :expanded.sync="expanded"
          @item-expanded="onExpand"
          :loading="loading"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          disable-sort
          dense
          class="bm-table bm-groups"
          :item-class="groupClass"
        >
          <template v-slot:item.number="{ item }">
            <div class="bm-cell">
              <strong>{{ item.number }}</strong>
              <span v-if="bucket === 'COMMITTED'" class="bm-tiny">{{
                item.approved === false && item.name === "PR belum ada PO"
                  ? "PR · belum ada PO"
                  : "PO · belum approve"
              }}</span>
              <span v-else-if="item.fxMissing" class="bm-tiny error--text"
                >kurs tidak ditemukan</span
              >
            </div>
          </template>
          <template v-slot:item.name="{ item }">
            <div v-if="isBom" class="bm-cell">
              <span v-if="item.poPartNumber" :title="item.name">{{ item.name }}</span>
              <v-chip v-else x-small color="grey lighten-3" class="align-self-start"
                >belum terhubung ke item PO</v-chip
              >
              <span class="bm-tiny">{{
                item.poPartNumber
                  ? "proyeksi masuk ke PO itu lewat panel code"
                  : "proyeksi mengikuti bagian budget job"
              }}</span>
            </div>
            <div v-else class="bm-cell">
              <span :class="{ 'grey--text': !item.name || item.name === 'PR belum ada PO' }">{{
                item.name && item.name !== "PR belum ada PO" ? item.name : "belum ada supplier"
              }}</span>
              <span v-if="item.description" class="bm-tiny">{{ item.description }}</span>
            </div>
          </template>
          <template v-slot:item.bomCovered="{ item }">
            <span class="bm-num grey--text text--darken-1">
              {{ bomCoveredOf(item) > 0 ? "− " + formatIdr(bomCoveredOf(item)) : "0" }}
            </span>
          </template>
          <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          <template v-slot:item.currency="{ item }">
            <v-chip
              v-if="item.currency && item.currency !== 'IDR'"
              x-small
              color="orange lighten-5"
              class="orange--text text--darken-3"
              >{{ item.currency }}</v-chip
            >
            <span v-else>{{ item.currency || "IDR" }}</span>
          </template>
          <template v-slot:item.rate="{ item }">
            <span :class="{ 'grey--text': Number(item.rate) === 1 }">{{
              formatIdr(item.rate)
            }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              :color="item.name === 'PR belum ada PO' ? 'grey lighten-3' : 'orange lighten-5'"
              :class="item.name === 'PR belum ada PO' ? '' : 'orange--text text--darken-3'"
            >
              {{ item.name === "PR belum ada PO" ? "PR belum ada PO" : "PO belum approve" }}
            </v-chip>
          </template>
          <template v-slot:item.job="{ item }">
            <div class="bm-cell">
              <span v-for="j in jobsOf(item)" :key="j.id">
                <router-link
                  v-if="noPo"
                  :to="'/report/budget-monitoring/job/' + j.id"
                  class="text-decoration-none"
                  >{{ j.id }}</router-link
                >
                <v-chip v-else x-small>{{ j.id }}</v-chip>
                <span v-if="j.name" class="bm-tiny"> {{ j.name }}</span>
              </span>
            </div>
          </template>
          <template v-slot:item.journal="{ item }">
            <span v-if="journalOf(item)">JV #{{ journalOf(item) }}</span>
            <v-chip v-else x-small color="orange lighten-5" class="orange--text text--darken-3"
              >belum dijurnal</v-chip
            >
          </template>
          <template v-slot:item.journalAmountIdr="{ item }">
            <span class="bm-num">{{ formatIdr(item.journalAmountIdr) }}</span>
          </template>
          <template v-slot:item.dedup="{ item }">
            <span class="bm-num grey--text text--darken-1">{{
              dedupOf(item) > 0 ? "− " + formatIdr(dedupOf(item)) : "0"
            }}</span>
          </template>
          <template v-slot:item.qtyGross="{ item }">{{
            formatQty(sumBy(item.lines, "qtyGross"))
          }}</template>
          <template v-slot:item.qtyPo="{ item }">{{
            formatQty(sumBy(item.lines, "qtyGross") - sumBy(item.lines, "qty"))
          }}</template>
          <template v-slot:item.qty="{ item }">
            <span :class="{ 'bm-strike': sumBy(item.lines, 'qty') === 0 }">{{
              formatQty(sumBy(item.lines, "qty"))
            }}</span>
          </template>
          <template v-slot:item.avgPrice="{ item }">{{ formatIdr(avgPrice(item)) }}</template>
          <template v-slot:item.mrCount="{ item }">
            <v-chip x-small color="indigo lighten-5" class="indigo--text">{{
              distinctMr(item)
            }}</v-chip>
          </template>
          <template v-slot:item.itemCount="{ item }">{{ item.itemCount }}</template>
          <template v-slot:item.amountIdr="{ item }">
            <span
              class="bm-num font-weight-medium"
              :class="{
                'bm-strike': isStock && Number(item.amountIdr) === 0 && dedupOf(item) > 0,
                'grey--text text--darken-1': bucket === 'COMMITTED',
              }"
            >
              <v-icon
                v-if="item.estimated"
                x-small
                color="warning"
                class="mr-1"
                title="alokasi estimasi"
                >mdi-approximately-equal</v-icon
              >{{ formatIdr(item.amountIdr) }}
            </span>
          </template>

          <!-- lines -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="bm-sub">
                <div class="d-flex align-center mb-2" style="gap: 12px">
                  <span class="bm-cap">{{ subCaption(item) }}</span>
                  <span class="bm-tiny bm-ell">{{ subHint }}</span>
                </div>
                <div v-if="item.linesLoaded === null" class="bm-tiny py-3">
                  <v-progress-circular
                    indeterminate
                    size="16"
                    width="2"
                    color="primary"
                    class="mr-2"
                  ></v-progress-circular>
                  Memuat item…
                </div>
                <v-simple-table v-else dense class="bm-fixed">
                  <thead>
                    <tr>
                      <th
                        v-for="h in lineHeaders"
                        :key="h.value"
                        :class="h.align === 'end' ? 'text-right' : ''"
                        :style="h.width ? 'width:' + h.width + 'px' : ''"
                      >
                        {{ h.text }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="l in linesOf(item)"
                      :key="lineKey(l)"
                      :class="{ 'grey--text': isStock && Number(l.qty) === 0 }"
                    >
                      <template v-if="isBom">
                        <td>
                          {{ clean(l.partNum) || (l.productId > 0 ? "#" + l.productId : "—") }}
                        </td>
                        <td>
                          <div class="bm-cell">
                            <span :class="{ 'grey--text': !itemName(l) }">{{
                              itemName(l) || "(nama tidak ada)"
                            }}</span>
                            <span class="bm-tiny">{{ itemNote(l) }}</span>
                          </div>
                        </td>
                        <td class="text-right">{{ formatQty(l.qtyGross) }}</td>
                        <td class="text-right">{{ formatQty(l.coveredMrQty || 0) }}</td>
                        <td class="text-right">{{ formatQty(l.coveredPoQty || 0) }}</td>
                        <td class="text-right">{{ formatQty(l.coveredPrQty || 0) }}</td>
                        <td class="text-right font-weight-medium">{{ formatQty(l.qty) }}</td>
                        <td class="text-right">
                          <div v-if="bomHasPrice(l)" class="bm-cell bm-cell--end">
                            <span>{{ formatIdr(l.unitPrice) }}</span>
                            <span class="bm-tiny">{{ bomPriceSourceLabel(l.priceSource) }}</span>
                          </div>
                          <v-chip
                            v-else
                            x-small
                            color="orange lighten-5"
                            class="orange--text text--darken-3"
                            >tanpa harga</v-chip
                          >
                        </td>
                        <td class="text-right">
                          <span v-if="bomHasPrice(l)" class="font-weight-medium">{{
                            formatIdr(l.amountIdr)
                          }}</span>
                          <span v-else class="grey--text">tidak dihitung</span>
                        </td>
                      </template>
                      <template v-else-if="isItem">
                        <td class="bm-ell" :title="l.mrNumber">{{ l.mrNumber || "—" }}</td>
                        <td>{{ formatDate(l.mrPickDate) }}</td>
                        <td>
                          <v-chip x-small>{{ l.jobId }}</v-chip>
                        </td>
                        <td>
                          <span v-if="l.mrJournalId">JV #{{ l.mrJournalId }}</span
                          ><span v-else class="orange--text text--darken-3">belum dijurnal</span>
                        </td>
                        <td class="text-right">{{ formatQty(l.qtyGross) }}</td>
                        <td class="text-right">
                          {{ formatQty(Number(l.qtyGross || 0) - Number(l.qty || 0)) }}
                        </td>
                        <td class="text-right" :class="{ 'bm-strike': Number(l.qty) === 0 }">
                          {{ formatQty(l.qty) }}
                        </td>
                        <td class="text-right">{{ formatIdr(l.unitPrice) }}</td>
                        <td class="text-right" :class="{ 'bm-strike': Number(l.qty) === 0 }">
                          {{ formatIdr(l.amountIdr) }}
                        </td>
                      </template>
                      <template v-else-if="isStock">
                        <td>
                          {{ clean(l.partNum) || (l.productId > 0 ? "#" + l.productId : "—") }}
                        </td>
                        <td>
                          <div class="bm-cell">
                            <span :class="{ 'grey--text': !itemName(l) }">{{
                              itemName(l) || "(nama tidak ada)"
                            }}</span>
                            <span class="bm-tiny">{{ itemNote(l) }}</span>
                          </div>
                        </td>
                        <td class="text-right">{{ formatQty(l.qtyGross) }}</td>
                        <td class="text-right">
                          {{ formatQty(Number(l.qtyGross || 0) - Number(l.qty || 0)) }}
                        </td>
                        <td class="text-right" :class="{ 'bm-strike': Number(l.qty) === 0 }">
                          {{ formatQty(l.qty) }}
                        </td>
                        <td class="text-right">{{ formatIdr(l.unitPrice) }}</td>
                        <td>
                          <div class="bm-cell" style="align-items: flex-start; white-space: normal">
                            <line-flags :line="l" stock></line-flags>
                            <span v-if="l.panelPoPartNumber" class="bm-tiny"
                              >item PO {{ l.panelPoPartNumber }}</span
                            >
                          </div>
                        </td>
                        <td class="text-right" :class="{ 'bm-strike': Number(l.qty) === 0 }">
                          {{ formatIdr(l.amountIdr) }}
                        </td>
                      </template>
                      <template v-else>
                        <td>
                          {{ clean(l.partNum) || (l.productId > 0 ? "#" + l.productId : "—") }}
                        </td>
                        <td>
                          <div class="bm-cell">
                            <span :class="{ 'grey--text': !itemName(l) }">{{
                              itemName(l) || "(nama tidak ada)"
                            }}</span>
                            <span class="bm-tiny">{{ itemNote(l) }}</span>
                          </div>
                        </td>
                        <td class="text-right">{{ formatQty(l.qty) }}</td>
                        <td class="text-right">{{ formatIdr(l.unitPrice) }}</td>
                        <td>{{ l.currency || "IDR" }}</td>
                        <td class="text-right">
                          <div class="bm-cell bm-cell--end">
                            <span :class="{ 'grey--text': Number(l.rateToIdr) === 1 }">{{
                              formatIdr(l.rateToIdr)
                            }}</span>
                            <span v-if="l.currency && l.currency !== 'IDR'" class="bm-tiny">{{
                              formatDate(l.rateDate)
                            }}</span>
                          </div>
                        </td>
                        <td>
                          <span v-if="l.prNumber">{{ l.prNumber }}</span
                          ><span v-else class="grey--text">—</span>
                          <line-flags :line="l"></line-flags>
                        </td>
                        <td
                          class="text-right"
                          :class="{ 'grey--text text--darken-1': bucket === 'COMMITTED' }"
                        >
                          {{ formatIdr(l.amountIdr) }}
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="py-2 px-6 bm-tiny flex-wrap" style="gap: 20px">
        <span v-if="isStock"
          >Baris <span class="bm-strike">dicoret</span> = terdedup, sudah dihitung di Belanja
          PO.</span
        >
        <span v-else-if="bucket === 'COMMITTED'"
          >Begitu PO di-approve, baris pindah ke tab Belanja dan masuk rasio pada perhitungan
          berikutnya.</span
        >
        <span v-else-if="isBom"
          >Proyeksi masuk ke PO lewat tautan panel code → item PO; tanpa tautan mengikuti bagian
          budget job. Stok bebas gudang tidak dikurangkan.
          <a :href="ppicShortageUrl" target="_blank" rel="noopener" class="text-decoration-none"
            >Lihat shortage BOM di PPIC <v-icon x-small color="primary">mdi-open-in-new</v-icon></a
          ></span
        >
        <span v-else-if="noPo"
          >Cara memperbaiki: hubungkan job ke PO customer di PPIC; pada perhitungan berikutnya
          angkanya pindah ke PO itu.</span
        >
        <span v-else
          >Freight termasuk Rp {{ formatIdr(freightTotal) }} · diskon Rp
          {{ formatIdr(discountTotal) }} · PPN tidak dihitung. Mata uang asing dikonversi memakai
          kurs tanggal PO.</span
        >
        <v-spacer></v-spacer>
        <v-btn
          v-if="context.crmPoId"
          color="success"
          text
          :loading="exporting"
          @click="exportExcel"
        >
          <v-icon left>mdi-file-excel</v-icon>Ekspor Excel
        </v-btn>
        <v-btn color="primary" text @click="$emit('input', false)">Tutup</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import fileDownload from "js-file-download";
import { VChip } from "vuetify/lib";
import {
  amountsMatch,
  bomHasPrice,
  bomNeedIdr,
  bomPriceSourceLabel,
  formatDate,
  formatIdr,
  formatPct,
  formatQty,
  isEstimatedLine,
  methodLabel,
  sumBy,
} from "@/utils/budgetMonitor";
import { PPIC_UI } from "@/services/config";
import "./budget-monitor.css";

/** Chips that explain a line's allocation: estimate share, cross job, unvalidated PR, price source, dedup. */
const LineFlags = {
  name: "line-flags",
  functional: true,
  props: { line: Object, stock: Boolean },
  render(h, { props }) {
    const l = props.line || {};
    const chips = [];
    const chip = (text, color, cls, title) =>
      chips.push(
        h(VChip, { props: { xSmall: true, color }, class: ["ml-1", cls], attrs: { title } }, text)
      );
    if (props.stock && l.dedupSource && Number(l.qty) === 0) {
      chip(
        "terdedup · " + l.dedupSource,
        "grey lighten-3",
        "",
        "qty MR sudah dibeli lewat PO supplier job ini"
      );
    } else if (props.stock && l.panelCodeId) {
      chip(
        (l.panelCodeName || "panel #" + l.panelCodeId) + " · " + methodLabel(l.allocationMethod),
        "green lighten-5",
        "green--text text--darken-3",
        l.panelPoPartNumber
          ? "panel code " + (l.panelCodeName || l.panelCodeId) + " → item PO " + l.panelPoPartNumber
          : "dialokasikan lewat panel code"
      );
    } else if (props.stock) {
      chip(
        methodLabel(l.allocationMethod) +
          (Number(l.allocationShare) < 1 ? " " + Math.round(l.allocationShare * 100) + " %" : ""),
        "blue lighten-5",
        "blue--text text--darken-3",
        "metode alokasi"
      );
    } else if (isEstimatedLine(l)) {
      chip(
        "estimasi " + Math.round(Number(l.allocationShare) * 100) + " %",
        "orange lighten-5",
        "orange--text text--darken-3",
        methodLabel(l.allocationMethod)
      );
    }
    if (l.crossJobHint)
      chip(
        "lintas job",
        "orange lighten-4",
        "",
        "PR dari " + l.crossJobHint + " yang tidak terhubung ke PO ini"
      );
    if (l.productUnvalidated)
      chip("belum validasi", "red lighten-4", "", "PR detail belum divalidasi (product_id 0)");
    if (l.priceSource && !["PO", "SNAPSHOT", "JOURNAL"].includes(l.priceSource))
      chip("harga " + l.priceSource, "blue-grey lighten-4", "", "sumber harga");
    return chips.length ? h("span", { class: "bm-flags" }, chips) : null;
  },
};

const MODES = {
  SPEND: {
    title: "Belanja PO supplier",
    mdi: "mdi-cart-outline",
    avatar: "blue lighten-5",
    icon: "blue darken-2",
    description:
      "PO supplier yang dialokasikan ke PO customer ini. Jumlah semua baris = angka <strong>Belanja PO</strong> di daftar dan detail.",
  },
  COMMITTED: {
    title: "Committed",
    mdi: "mdi-clock-outline",
    avatar: "blue-grey lighten-5",
    icon: "blue-grey darken-2",
    description:
      "Belanja yang sudah dijanjikan tapi belum jadi PO approve: PR tanpa PO dan PO supplier yang belum approve. <strong>Tidak masuk rasio</strong> — informasi agar tahu tagihan yang akan datang.",
  },
  STOCK: {
    title: "Pemakaian stok gudang",
    mdi: "mdi-package-variant-closed",
    avatar: "indigo lighten-5",
    icon: "indigo darken-2",
    description:
      "Material Request (MR) job yang terhubung ke PO ini. Yang masuk rasio adalah <strong>neto</strong>: nilai MR dikurangi item yang sudah dibeli lewat PO supplier job ini, agar tidak dihitung dua kali.",
  },
  ITEM: {
    title: "Pemakaian stok gudang",
    mdi: "mdi-package-variant-closed",
    avatar: "indigo lighten-5",
    icon: "indigo darken-2",
    description:
      "Tampilan per item: satu baris = satu barang gudang, dijumlahkan dari semua MR. Berguna untuk melihat barang apa yang paling menyerap budget.",
  },
  BOM: {
    title: "Sisa BOM belum dibeli / di-MR",
    mdi: "mdi-clipboard-list-outline",
    avatar: "pink lighten-5",
    icon: "pink darken-4",
    description:
      "Per item BOM tiap panel code: <strong>kebutuhan BOM − (sudah di-MR + PO supplier + PR)</strong>. Sisa × harga beli terakhir = perkiraan belanja yang masih akan datang. Ini proyeksi, bukan tagihan — <strong>tidak masuk rasio</strong>.",
  },
  NOPO: {
    title: "Belanja & stok tanpa PO CRM",
    mdi: "mdi-link-variant-off",
    avatar: "orange lighten-5",
    icon: "orange darken-3",
    description:
      "Job PPIC yang belum terhubung ke PO customer mana pun. Angkanya <strong>tidak masuk budget mana pun</strong> sampai job dihubungkan ke PO di PPIC.",
  },
};

export default {
  name: "budget-spend-breakdown",
  components: { LineFlags },
  props: {
    value: Boolean,
    /**
     * { crmPoId, jobId, bucket: 'SPEND'|'COMMITTED'|'STOCK'|'BOM', expectedAmount, poNumber, jobName,
     *   jobs: [{jobId, jobName}], estimatedPercent, budgetIdr, usageIdr, noPo, panelCodeId }
     */
    context: { type: Object, required: true },
  },
  data: () => ({
    groups: [],
    expanded: [],
    loading: false,
    exporting: false,
    bucket: "SPEND",
    groupBy: "MR",
    jobFilter: null,
    panelFilter: null,
    search: "",
    hideDedup: false,
    noPoTotals: { SPEND: 0, STOCK: 0 },
    noPoLoaded: { SPEND: false, STOCK: false },
    noPoJobCount: 0,
  }),
  computed: {
    noPo() {
      return !!this.context.noPo;
    },
    isStock() {
      return this.bucket === "STOCK";
    },
    isItem() {
      return this.isStock && this.groupBy === "ITEM";
    },
    isBom() {
      return this.bucket === "BOM";
    },
    ppicShortageUrl() {
      const base = PPIC_UI[process.env.VUE_APP_API_ENV] || PPIC_UI.default;
      return base + "/#/mpsdashboard";
    },
    panelOptions() {
      const items = this.groups.map(g => ({
        value: g.key,
        text: g.number + (g.poPartNumber ? " · " + g.poPartNumber : ""),
      }));
      return items.length > 1
        ? [{ value: null, text: `Semua (${items.length})` }, ...items]
        : items;
    },
    /** BOM: Σ need × price of the priced lines, and what the job already covers of it */
    bomNeedTotal() {
      return this.allLines.filter(l => bomHasPrice(l)).reduce((t, l) => t + bomNeedIdr(l), 0);
    },
    bomCoveredTotal() {
      return Math.max(0, this.bomNeedTotal - this.sum);
    },
    bomItemCount() {
      return new Set(this.allLines.map(l => l.productId)).size;
    },
    bomNoPriceCount() {
      return new Set(this.allLines.filter(l => !bomHasPrice(l)).map(l => l.productId)).size;
    },
    mode() {
      if (this.noPo) return MODES.NOPO;
      if (this.isItem) return MODES.ITEM;
      return MODES[this.bucket] || MODES.SPEND;
    },
    subjectLabel() {
      if (this.noPo) return `${this.noPoJobCount} job`;
      if (this.context.crmPoId) {
        const po = "PO " + (this.context.poNumber || "#" + this.context.crmPoId);
        return this.context.jobId ? `${po} · job ${this.context.jobId}` : po;
      }
      return `job ${this.context.jobId}${this.context.jobName ? " · " + this.context.jobName : ""}`;
    },
    toLabel() {
      return this.context.crmPoId ? "ke PO ini" : "job ini";
    },
    searchLabel() {
      return this.isItem || this.isBom
        ? "Cari part number / nama / deskripsi"
        : this.isStock
        ? "Cari nomor MR / part number / nama"
        : "Cari nomor PO supplier / supplier / part";
    },
    jobOptions() {
      const ids = new Set();
      this.groups.forEach(g =>
        String(g.job || "")
          .split(",")
          .forEach(x => x && ids.add(x))
      );
      const names = {};
      (this.context.jobs || []).forEach(j => (names[String(j.jobId)] = j.jobName));
      const items = [...ids].map(id => ({
        value: id,
        text: names[id] ? `${id} · ${names[id]}` : id,
      }));
      return items.length > 1
        ? [{ value: null, text: `Semua (${items.length})` }, ...items]
        : items;
    },
    groupsFiltered() {
      const q = (this.search || "").trim().toLowerCase();
      return this.groups
        .filter(g => !this.panelFilter || g.key === this.panelFilter)
        .filter(
          g =>
            !this.jobFilter ||
            String(g.job || "")
              .split(",")
              .includes(String(this.jobFilter))
        )
        .filter(g => {
          if (!q) return true;
          const head = [g.number, g.name, g.description, g.job].join(" ").toLowerCase();
          return (
            head.includes(q) ||
            (g.lines || []).some(l =>
              [l.partNum, l.partName, l.partDesc, l.mrNumber, l.prNumber]
                .join(" ")
                .toLowerCase()
                .includes(q)
            )
          );
        })
        .filter(
          g => !(this.hideDedup && this.isStock && Number(g.amountIdr) === 0 && this.dedupOf(g) > 0)
        );
    },
    sum() {
      return sumBy(this.groupsFiltered, "amountIdr");
    },
    journalTotal() {
      if (this.isItem) {
        return this.allLines.reduce(
          (t, l) => t + Number(l.qtyGross || 0) * Number(l.unitPrice || 0),
          0
        );
      }
      return sumBy(this.groupsFiltered, "journalAmountIdr");
    },
    dedupTotal() {
      return Math.max(0, this.journalTotal - this.sum);
    },
    mrCount() {
      if (this.groupBy === "MR") return this.groupsFiltered.length; // one group per MR (lines may be lazy)
      const ids = new Set();
      this.groupsFiltered.forEach(g => (g.lines || []).forEach(l => l.mrId && ids.add(l.mrId)));
      return ids.size;
    },
    matchCopy() {
      if (this.jobFilter || this.panelFilter || this.search || this.hideDedup)
        return "sebagian (ada filter)";
      return amountsMatch(this.sum, this.context.expectedAmount)
        ? "sama dengan angka di daftar"
        : "berbeda dari angka di daftar";
    },
    jobCountLabel() {
      const n = new Set();
      this.groupsFiltered.forEach(g =>
        String(g.job || "")
          .split(",")
          .forEach(x => x && n.add(x))
      );
      return `${n.size} job`;
    },
    estimatedCopy() {
      const est = Number(this.context.estimatedPercent || 0);
      return est > 0 ? `alokasi estimasi ${Math.round(est)} %` : "alokasi pasti 100 %";
    },
    committedRatio() {
      const b = Number(this.context.budgetIdr || 0);
      if (!b) return null;
      return ((Number(this.context.usageIdr || 0) + this.sum) / b) * 100;
    },
    allLines() {
      return this.groupsFiltered.reduce((acc, g) => acc.concat(g.lines || []), []);
    },
    freightTotal() {
      return sumBy(this.allLines, "freightIdr");
    },
    discountTotal() {
      return sumBy(this.allLines, "discountIdr");
    },
    groupHeaders() {
      const expand = { text: "", value: "data-table-expand", width: 32 };
      if (this.isBom) {
        return [
          expand,
          { text: "Panel code", value: "number", width: 150 },
          { text: "Item PO customer", value: "name" },
          { text: "Job", value: "job", width: this.context.crmPoId ? 110 : 90 },
          { text: "Item", value: "itemCount", align: "end", width: 70 },
          { text: "Kebutuhan BOM", value: "journalAmountIdr", align: "end", width: 150 },
          { text: "Tercakup", value: "bomCovered", align: "end", width: 140 },
          { text: "Sisa × harga (IDR)", value: "amountIdr", align: "end", width: 160 },
        ];
      }
      if (this.isItem) {
        return [
          expand,
          { text: "Part number", value: "number", width: 150 },
          { text: "Nama · deskripsi", value: "name" },
          { text: "Qty MR", value: "qtyGross", align: "end", width: 90 },
          { text: "Qty PO job", value: "qtyPo", align: "end", width: 100 },
          { text: "Qty neto", value: "qty", align: "end", width: 90 },
          { text: "Harga rata-rata", value: "avgPrice", align: "end", width: 120 },
          { text: "MR", value: "mrCount", align: "end", width: 70 },
          { text: "Neto " + this.toLabel + " (IDR)", value: "amountIdr", align: "end", width: 160 },
        ];
      }
      if (this.isStock) {
        return [
          expand,
          { text: "MR", value: "number", width: 180 },
          { text: "Tgl pick", value: "date", width: 110 },
          { text: "Job", value: "job", width: this.noPo ? 220 : 110 },
          { text: "Jurnal accounting", value: "journal" },
          { text: "Nilai jurnal MR", value: "journalAmountIdr", align: "end", width: 150 },
          { text: "Terdedup", value: "dedup", align: "end", width: 130 },
          { text: "Neto " + this.toLabel + " (IDR)", value: "amountIdr", align: "end", width: 160 },
        ];
      }
      if (this.bucket === "COMMITTED") {
        return [
          expand,
          { text: "Dokumen", value: "number", width: 180 },
          { text: "Supplier", value: "name" },
          { text: "Tanggal", value: "date", width: 110 },
          { text: "Status", value: "status", width: 170 },
          { text: "Job", value: "job", width: 110 },
          {
            text: "Nilai " + this.toLabel + " (IDR)",
            value: "amountIdr",
            align: "end",
            width: 160,
          },
        ];
      }
      return [
        expand,
        { text: "PO supplier", value: "number", width: 180 },
        { text: "Supplier", value: "name" },
        { text: "Tgl PO", value: "date", width: 110 },
        { text: "Curr", value: "currency", width: 70 },
        { text: "Kurs", value: "rate", align: "end", width: 90 },
        {
          text: this.noPo ? "Job (tanpa PO CRM)" : "Job",
          value: "job",
          width: this.noPo ? 220 : 110,
        },
        { text: "Item", value: "itemCount", align: "end", width: 60 },
        {
          text: (this.noPo ? "Nilai" : "Nilai " + this.toLabel) + " (IDR)",
          value: "amountIdr",
          align: "end",
          width: 160,
        },
      ];
    },
    lineHeaders() {
      if (this.isBom) {
        return [
          { text: "Part number", value: "pn", width: 140 },
          { text: "Nama · deskripsi", value: "name" },
          { text: "Kebutuhan", value: "need", align: "end", width: 90 },
          { text: "MR", value: "mr", align: "end", width: 70 },
          { text: "PO", value: "po", align: "end", width: 70 },
          { text: "PR", value: "pr", align: "end", width: 70 },
          { text: "Sisa", value: "qty", align: "end", width: 80 },
          { text: "Harga terakhir", value: "price", align: "end", width: 140 },
          { text: "Proyeksi (IDR)", value: "amount", align: "end", width: 150 },
        ];
      }
      if (this.isItem) {
        return [
          { text: "MR", value: "mr", width: 180 },
          { text: "Tgl pick", value: "date", width: 110 },
          { text: "Job", value: "job", width: 100 },
          { text: "Jurnal", value: "journal" },
          { text: "Qty MR", value: "qtyGross", align: "end", width: 90 },
          { text: "Qty PO job", value: "qtyPo", align: "end", width: 100 },
          { text: "Qty neto", value: "qty", align: "end", width: 90 },
          { text: "Harga satuan", value: "price", align: "end", width: 132 },
          { text: "Neto (IDR)", value: "amount", align: "end", width: 150 },
        ];
      }
      if (this.isStock) {
        return [
          { text: "Part number", value: "pn", width: 140 },
          { text: "Nama · deskripsi", value: "name" },
          { text: "Qty MR", value: "qtyGross", align: "end", width: 80 },
          { text: "Qty PO job", value: "qtyPo", align: "end", width: 90 },
          { text: "Qty neto", value: "qty", align: "end", width: 80 },
          { text: "Harga satuan", value: "price", align: "end", width: 120 },
          { text: "Panel / alokasi", value: "flags", width: 230 },
          { text: "Neto " + this.toLabel + " (IDR)", value: "amount", align: "end", width: 150 },
        ];
      }
      return [
        { text: "Part number", value: "pn", width: 140 },
        { text: "Nama · deskripsi", value: "name" },
        { text: "Qty", value: "qty", align: "end", width: 70 },
        { text: "Harga satuan", value: "price", align: "end", width: 120 },
        { text: "Curr", value: "curr", width: 60 },
        { text: "Kurs · tgl", value: "rate", align: "end", width: 110 },
        { text: "PR asal · alokasi", value: "pr", width: 220 },
        {
          text: (this.noPo ? "Nilai" : "Nilai " + this.toLabel) + " (IDR)",
          value: "amount",
          align: "end",
          width: 150,
        },
      ];
    },
    subHint() {
      if (this.isBom)
        return "MR / PO / PR = bagian panel code ini dari qty yang sudah tercakup di job (kebutuhan − MR − PO − PR = sisa, tidak pernah negatif). Harga = PO / PR / MR job ini, else pembelian terakhir.";
      if (this.isItem)
        return "Harga per MR bisa berbeda (snapshot PPIC saat pick, diskalakan ke nilai jurnal).";
      if (this.isStock)
        return "Qty neto = qty MR − qty yang sudah dibeli PO supplier job ini. Harga = snapshot PPIC, diskalakan ke nilai jurnal.";
      if (this.bucket === "COMMITTED")
        return "Harga = harga PR / PO belum approve. Kurs tanggal dokumen.";
      return "Nilai IDR = qty × harga × kurs + freight − diskon. PPN tidak dihitung.";
    },
  },
  watch: {
    value(v) {
      if (v) this.reset();
    },
  },
  methods: {
    formatIdr,
    formatPct,
    formatQty,
    formatDate,
    sumBy,
    bomHasPrice,
    bomPriceSourceLabel,
    /** need value already covered by MR / PO / PR: need − remaining (priced lines only) */
    bomCoveredOf(g) {
      const need = (g.lines || [])
        .filter(l => bomHasPrice(l))
        .reduce((t, l) => t + bomNeedIdr(l), 0);
      return Math.max(0, need - Number(g.amountIdr || 0));
    },
    /** PPIC sends "none" / "-" for missing part numbers and names. */
    clean(v) {
      const t = v === null || v === undefined ? "" : String(v).trim();
      return ["", "none", "null", "-"].includes(t.toLowerCase()) ? "" : t;
    },
    /** Name from the item master, else the PR / MR description so the row still says what was bought. */
    itemName(l) {
      return this.clean(l.partName) || this.clean(l.partDesc);
    },
    /** Second line: the description when it is not already the name, plus the product id when the master has no name. */
    itemNote(l) {
      const parts = [];
      if (this.clean(l.partName) && this.clean(l.partDesc)) parts.push(this.clean(l.partDesc));
      if (!this.clean(l.partName)) {
        parts.push(
          l.productId > 0
            ? `produk #${l.productId} · nama tidak ada di item master gudang`
            : "produk belum divalidasi (product id 0) · dari PR"
        );
      }
      return parts.join(" · ");
    },
    reset() {
      this.bucket = this.context.bucket || "SPEND";
      this.groupBy = "MR";
      this.jobFilter = null;
      this.panelFilter = null;
      this.search = "";
      this.hideDedup = false;
      this.groups = [];
      this.noPoTotals = { SPEND: 0, STOCK: 0 };
      this.noPoLoaded = { SPEND: false, STOCK: false };
      this.noPoJobCount = 0;
      this.load();
    },
    lineKey(l) {
      return l.id || [l.prDetailId, l.posId, l.mrId, l.productId].join("-");
    },
    groupClass(g) {
      return this.expanded.includes(g) ? "row-open" : "";
    },
    jobsOf(g) {
      const names = {};
      (this.context.jobs || []).forEach(j => (names[String(j.jobId)] = j.jobName));
      return String(g.job || "")
        .split(",")
        .filter(x => x)
        .map(id => ({ id, name: this.noPo ? names[id] || "" : "" }));
    },
    journalOf(g) {
      const l = (g.lines || []).find(x => x.mrJournalId);
      return l ? l.mrJournalId : null;
    },
    dedupOf(g) {
      return Math.max(0, Number(g.journalAmountIdr || 0) - Number(g.amountIdr || 0));
    },
    avgPrice(g) {
      const qty = sumBy(g.lines, "qtyGross");
      const value = (g.lines || []).reduce(
        (s, l) => s + Number(l.qtyGross || 0) * Number(l.unitPrice || 0),
        0
      );
      return qty > 0 ? value / qty : null;
    },
    distinctMr(g) {
      return new Set((g.lines || []).map(l => l.mrId).filter(x => x)).size;
    },
    linesOf(g) {
      const lines = g.lines || [];
      return this.hideDedup && this.isStock
        ? lines.filter(l => !(l.dedupSource && Number(l.qty) === 0))
        : lines;
    },
    subCaption(g) {
      if (this.isBom) return `ITEM BOM ${g.number} · ${(g.lines || []).length} ITEM MASIH ADA SISA`;
      if (this.isItem) return `MR ASAL · ${g.number} · ${this.distinctMr(g)} MR`;
      return `ITEM ${g.number} · ${(g.lines || []).length} ITEM`;
    },
    async load() {
      this.loading = true;
      this.groups = [];
      try {
        const params = {
          bucket: this.bucket,
          groupBy: this.isBom ? "PANEL" : this.isStock ? this.groupBy : "POS",
        };
        let res;
        if (this.noPo) {
          // ~15 000 lines without a PO: fetch the groups only, lines arrive when a row is expanded
          res = await this.$store.dispatch("budgetMonitor/fetchNoJobLines", {
            bucket: this.bucket,
            groupBy: params.groupBy,
            withLines: false,
          });
          res = (res || []).map(g => ({ ...g, lines: [], linesLoaded: false }));
        } else if (this.context.crmPoId) {
          res = await this.$store.dispatch("budgetMonitor/fetchPoLines", {
            crmPoId: this.context.crmPoId,
            jobId: this.context.jobId,
            ...params,
          });
        } else {
          res = await this.$store.dispatch("budgetMonitor/fetchJobLines", {
            jobId: this.context.jobId,
            ...params,
          });
        }
        this.groups = (res || []).map(g => ({
          ...g,
          estimated: (g.lines || []).some(l => isEstimatedLine(l)),
        }));
        if (this.isBom && this.context.panelCodeId) {
          const own = this.groups.find(g => g.panelCodeId === this.context.panelCodeId);
          this.panelFilter = own ? own.key : null;
        }
        this.expanded = this.groups.length ? [this.groups[0]] : [];
        if (this.isBom && this.panelFilter) {
          const own = this.groups.find(g => g.key === this.panelFilter);
          this.expanded = own ? [own] : this.expanded;
        }
        if (this.noPo && this.groups.length) this.loadGroupLines(this.groups[0]);
        if (this.noPo) {
          this.noPoLoaded[this.bucket] = true;
          this.loadNoPoTotals();
        }
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat rincian" });
      } finally {
        this.loading = false;
      }
    },
    /** Header figures of the no-PO dialog: the loaded bucket from `groups`, the other bucket fetched once. */
    onExpand({ item, value }) {
      if (value && this.noPo && item && item.linesLoaded === false) this.loadGroupLines(item);
    },
    /** No-PO dialog: fetch one group's lines the first time it is expanded. */
    async loadGroupLines(g) {
      if (!g || g.linesLoaded !== false) return;
      this.$set(g, "linesLoaded", null); // loading
      try {
        const full = await this.$store.dispatch("budgetMonitor/fetchNoJobGroup", {
          key: g.key,
          bucket: this.bucket,
          groupBy: this.isStock ? this.groupBy : "POS",
        });
        this.$set(g, "lines", (full && full.lines) || []);
        this.$set(
          g,
          "estimated",
          (g.lines || []).some(l => isEstimatedLine(l))
        );
        this.$set(g, "linesLoaded", true);
      } catch (e) {
        this.$set(g, "linesLoaded", false);
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat item" });
      }
    },
    async loadNoPoTotals() {
      const other = this.bucket === "STOCK" ? "SPEND" : "STOCK";
      const jobs = new Set();
      this.groups.forEach(g =>
        String(g.job || "")
          .split(",")
          .forEach(x => x && jobs.add(x))
      );
      const totals = { ...this.noPoTotals, [this.bucket]: sumBy(this.groups, "amountIdr") };
      if (!this.noPoLoaded[other]) {
        try {
          const rows = await this.$store.dispatch("budgetMonitor/fetchNoJobLines", other);
          totals[other] = sumBy(rows, "amountIdr");
          (rows || []).forEach(g =>
            String(g.job || "")
              .split(",")
              .forEach(x => x && jobs.add(x))
          );
          this.noPoLoaded[other] = true;
        } catch (e) {
          totals[other] = 0;
        }
      }
      this.noPoTotals = totals;
      this.noPoJobCount = Math.max(this.noPoJobCount, jobs.size);
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/exportPo", this.context.crmPoId);
        fileDownload(res.data, `budget-${this.context.poNumber || this.context.crmPoId}.xlsx`);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal mengekspor" });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.bm-head {
  display: flex;
  align-items: flex-start;
  padding: 18px 24px 14px;
}
.bm-head-title {
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.0125em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bm-total {
  font-size: 22px;
  font-weight: 500;
}
.bm-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 24px 12px;
}
.bm-w-170 {
  max-width: 170px;
}
.bm-w-260 {
  max-width: 260px;
}
.bm-body {
  min-height: 320px;
}
.bm-groups >>> tbody tr.row-open {
  background: #e3f2fd;
}
</style>
